/**
 * RGPD consent state — single source of truth for cookie/tracking consent.
 * Decision lives in localStorage `fh_consent`; a minimal anonymous log is sent
 * to /api/privacy/consent. Non-essential trackers (PostHog, UTM pings) only
 * run when the matching category is granted.
 */
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const CONSENT_KEY = 'fh_consent';
export const CONSENT_VERSION = '1.0-draft';
export const BANNER_VERSION = '1.0';

export function getConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const c = JSON.parse(raw);
    return c && c.categories ? c : null;
  } catch {
    return null;
  }
}

export function hasConsent(category) {
  if (category === 'essential') return true;
  const c = getConsent();
  return !!(c && c.categories && c.categories[category]);
}

export function consentHeaders() {
  const c = getConsent();
  if (!c) return {};
  const granted = ['analytics', 'marketing'].filter((k) => c.categories[k]);
  return granted.length ? { 'X-FH-Consent': granted.join(',') } : {};
}

function applyConsent(record) {
  const analytics = !!(record && record.categories && record.categories.analytics);
  try {
    if (analytics && typeof window.__fhLoadAnalytics === 'function') {
      window.__fhLoadAnalytics();
    } else if (!analytics && window.posthog && window.posthog.__loaded) {
      if (typeof window.posthog.stopSessionRecording === 'function') window.posthog.stopSessionRecording();
      window.posthog.opt_out_capturing();
    }
  } catch { /* never break the app over analytics */ }
}

export function saveConsent(categories, meta = {}) {
  const record = {
    version: CONSENT_VERSION,
    categories: {
      essential: true,
      analytics: !!categories.analytics,
      marketing: !!categories.marketing,
    },
    decided_at: new Date().toISOString(),
  };
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(record));
  } catch { /* storage may be unavailable */ }
  axios.post(`${BACKEND_URL}/api/privacy/consent`, {
    policy_version: CONSENT_VERSION,
    banner_version: BANNER_VERSION,
    choice: meta.choice || 'customize',
    lang: meta.lang || '',
    categories: record.categories,
  }).catch(() => {});
  applyConsent(record);
  try {
    window.dispatchEvent(new CustomEvent('fh:consent-changed', { detail: record }));
  } catch { /* noop */ }
  return record;
}

export function openConsentPreferences() {
  window.dispatchEvent(new CustomEvent('fh:open-consent'));
}
